import { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pause, Play, Trash2, Calendar, Edit2 } from "lucide-react";

const ALL_PLANS = [
  { key: "coaching_1sem_monthly", label: "Coaching · 1 séance/sem", type: "coaching", category: "Présentiel" },
  { key: "coaching_2sem_monthly", label: "Coaching · 2 séances/sem", type: "coaching", category: "Présentiel" },
  { key: "coaching_3sem_monthly", label: "Coaching · 3 séances/sem", type: "coaching", category: "Présentiel" },
  { key: "libre_1sem_monthly", label: "Accès libre · 1 séance/sem", type: "libre", category: "Accès Libre" },
  { key: "libre_2sem_monthly", label: "Accès libre · 2 séances/sem", type: "libre", category: "Accès Libre" },
  { key: "libre_3sem_monthly", label: "Accès libre · 3 séances/sem", type: "libre", category: "Accès Libre" },
  { key: "remote_decouverte", label: "Distanciel · Découverte", type: "distanciel", category: "Distanciel" },
  { key: "remote_evolution", label: "Distanciel · Évolution", type: "distanciel", category: "Distanciel" },
  { key: "remote_transformation", label: "Distanciel · Transformation", type: "distanciel", category: "Distanciel" },
];

const STATUS_CONFIG = {
  active: { label: "Actif", color: "bg-green-500", icon: "✓" },
  paused: { label: "En pause", color: "bg-yellow-500", icon: "⏸" },
  cancelled: { label: "Annulé", color: "bg-red-500", icon: "✕" },
};

export default function MemberSubscriptions({ memberId, memberName }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [quotas, setQuotas] = useState({ coaching: 0, libre: 0, distanciel: 0 });
  const [loading, setLoading] = useState(true);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editNotesModalOpen, setEditNotesModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState("");
  const [selectedSub, setSelectedSub] = useState(null);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (memberId) {
      loadSubscriptions();
      loadQuotas();
    }
  }, [memberId]);

  const loadSubscriptions = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/members/${memberId}/subscriptions`);
      setSubscriptions(res.data.subscriptions || []);
    } catch (err) {
      toast.error("Erreur lors du chargement des forfaits");
    } finally {
      setLoading(false);
    }
  };

  const loadQuotas = async () => {
    try {
      const res = await api.get(`/admin/members/${memberId}/quotas`);
      setQuotas(res.data.quotas || { coaching: 0, libre: 0, distanciel: 0 });
    } catch (err) {
      console.error("Erreur quotas:", err);
    }
  };

  const addSubscription = async () => {
    if (!selectedPlan) {
      toast.error("Veuillez sélectionner un forfait");
      return;
    }

    try {
      await api.post(`/admin/members/${memberId}/subscriptions`, {
        plan_id: selectedPlan,
        notes: "",
      });
      toast.success("Forfait ajouté avec succès");
      setAddModalOpen(false);
      setSelectedPlan("");
      loadSubscriptions();
      loadQuotas();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const updateSubscriptionStatus = async (subId, newStatus) => {
    try {
      await api.put(`/admin/subscriptions/${subId}`, { status: newStatus });
      toast.success(
        newStatus === "active" ? "Forfait réactivé" :
        newStatus === "paused" ? "Forfait mis en pause" :
        "Forfait annulé"
      );
      loadSubscriptions();
      loadQuotas();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const updateNotes = async () => {
    if (!selectedSub) return;
    
    try {
      await api.put(`/admin/subscriptions/${selectedSub.id}`, { notes });
      toast.success("Notes enregistrées");
      setEditNotesModalOpen(false);
      setSelectedSub(null);
      setNotes("");
      loadSubscriptions();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const deleteSubscription = async () => {
    if (!selectedSub) return;

    try {
      await api.delete(`/admin/subscriptions/${selectedSub.id}`);
      toast.success("Forfait supprimé");
      setDeleteDialogOpen(false);
      setSelectedSub(null);
      loadSubscriptions();
      loadQuotas();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const openEditNotes = (sub) => {
    setSelectedSub(sub);
    setNotes(sub.notes || "");
    setEditNotesModalOpen(true);
  };

  const openDeleteDialog = (sub) => {
    setSelectedSub(sub);
    setDeleteDialogOpen(true);
  };

  // Filter plans already active for this user
  const getAvailablePlans = () => {
    const activePlanIds = subscriptions
      .filter(s => s.status === "active")
      .map(s => s.plan_id);
    
    // Group by category to show warning if trying to add duplicate
    return ALL_PLANS;
  };

  if (loading) {
    return <div className="text-sm text-stone-500">Chargement des forfaits...</div>;
  }

  const activeSubs = subscriptions.filter(s => s.status === "active");
  const inactiveSubs = subscriptions.filter(s => s.status !== "active");

  return (
    <div className="space-y-6">
      {/* Quotas Summary */}
      <div className="rounded-lg border border-stone-200 bg-stone-50 p-4">
        <h4 className="text-sm font-medium text-stone-700 mb-3">Quotas hebdomadaires cumulés</h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="bg-white rounded-md p-3 border border-stone-200">
            <div className="text-xs text-stone-500 mb-1">Présentiel (Coaching)</div>
            <div className="text-2xl font-bold text-stone-900">{quotas.coaching}</div>
            <div className="text-xs text-stone-500">séances/semaine</div>
          </div>
          <div className="bg-white rounded-md p-3 border border-stone-200">
            <div className="text-xs text-stone-500 mb-1">Accès Libre</div>
            <div className="text-2xl font-bold text-stone-900">{quotas.libre}</div>
            <div className="text-xs text-stone-500">séances/semaine</div>
          </div>
          <div className="bg-white rounded-md p-3 border border-stone-200">
            <div className="text-xs text-stone-500 mb-1">Distanciel</div>
            <div className="text-2xl font-bold text-stone-900">{quotas.distanciel}</div>
            <div className="text-xs text-stone-500">séances/semaine</div>
          </div>
        </div>
      </div>

      {/* Active Subscriptions */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-stone-700">
            Forfaits actifs ({activeSubs.length})
          </h4>
          <Button 
            size="sm" 
            onClick={() => setAddModalOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Ajouter un forfait
          </Button>
        </div>

        {activeSubs.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-stone-300 rounded-lg">
            <p className="text-sm text-stone-500">Aucun forfait actif</p>
            <Button 
              size="sm" 
              variant="outline" 
              onClick={() => setAddModalOpen(true)}
              className="mt-2"
            >
              Ajouter le premier forfait
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {activeSubs.map((sub) => (
              <div
                key={sub.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-stone-200 rounded-lg bg-white hover:shadow-sm transition-shadow"
              >
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-stone-900">
                      {sub.plan_name}
                    </span>
                    <Badge className={`${STATUS_CONFIG[sub.status].color} text-white text-xs`}>
                      {STATUS_CONFIG[sub.status].icon} {STATUS_CONFIG[sub.status].label}
                    </Badge>
                    <span className="text-xs text-stone-500">
                      {sub.sessions_per_week} séance{sub.sessions_per_week > 1 ? 's' : ''}/sem
                    </span>
                  </div>
                  <div className="text-xs text-stone-500">
                    {sub.plan_price}€/mois • Depuis {new Date(sub.start_date).toLocaleDateString('fr-FR')}
                  </div>
                  {sub.notes && (
                    <div className="text-xs text-stone-600 bg-stone-50 p-2 rounded mt-2">
                      📝 {sub.notes}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                  {sub.type === "distanciel" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditNotes(sub)}
                      className="gap-1 text-xs"
                    >
                      <Edit2 className="h-3 w-3" />
                      Notes
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateSubscriptionStatus(sub.id, "paused")}
                    className="gap-1 text-xs"
                  >
                    <Pause className="h-3 w-3" />
                    Pause
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openDeleteDialog(sub)}
                    className="gap-1 text-xs text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                    Supprimer
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inactive Subscriptions */}
      {inactiveSubs.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-stone-700 mb-3">
            Forfaits inactifs ({inactiveSubs.length})
          </h4>
          <div className="space-y-2">
            {inactiveSubs.map((sub) => (
              <div
                key={sub.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-stone-200 rounded-lg bg-stone-50 opacity-75"
              >
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm text-stone-700">
                      {sub.plan_name}
                    </span>
                    <Badge className={`${STATUS_CONFIG[sub.status].color} text-white text-xs`}>
                      {STATUS_CONFIG[sub.status].icon} {STATUS_CONFIG[sub.status].label}
                    </Badge>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {sub.status === "paused" && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateSubscriptionStatus(sub.id, "active")}
                      className="gap-1 text-xs text-green-600"
                    >
                      <Play className="h-3 w-3" />
                      Réactiver
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openDeleteDialog(sub)}
                    className="gap-1 text-xs text-red-600"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add Subscription Modal */}
      <Dialog open={addModalOpen} onOpenChange={setAddModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Ajouter un forfait</DialogTitle>
            <DialogDescription>
              Sélectionnez un forfait pour {memberName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="plan-select">Forfait</Label>
              <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                <SelectTrigger id="plan-select">
                  <SelectValue placeholder="Choisir un forfait..." />
                </SelectTrigger>
                <SelectContent>
                  {getAvailablePlans().map((plan) => (
                    <SelectItem key={plan.key} value={plan.key}>
                      {plan.label} ({plan.category})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-500 mt-1">
                Vous pouvez ajouter plusieurs forfaits de catégories différentes ou identiques
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={addSubscription}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Notes Modal */}
      <Dialog open={editNotesModalOpen} onOpenChange={setEditNotesModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Notes du forfait Distanciel</DialogTitle>
            <DialogDescription>
              Ajoutez des notes de suivi (visibles uniquement dans l'Admin)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Ex: Programme personnalisé envoyé, prochain point prévu le..."
                rows={4}
                className="resize-none"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditNotesModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={updateNotes}>
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce forfait ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le forfait &quot;{selectedSub?.plan_name}&quot; sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSubscription} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
